import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { User } from '../types';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface AuthState {
  token: string | null;
  user: User | null;
  isSuspended: boolean;
}

const initialState: AuthState = {
  token: null,
  user: null,
  isSuspended: false,
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setCredentials: (state, action: PayloadAction<{ token: string; user: User }>) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.isSuspended = false;
    },
    signOut: (state) => {
      state.token = null;
      state.user = null;
      state.isSuspended = false;
    },
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    setAccountSuspended: (state, action: PayloadAction<boolean>) => {
      state.isSuspended = action.payload;
    },
  },
});

export const { setToken, setCredentials, signOut, updateUser, setAccountSuspended } =
  authSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// Typed locally (not against RootState) to avoid a circular dep with store/index.ts.
// store/baseApi.ts imports this slice — if this file imported RootState from
// @/store, that would create: baseApi → authSlice → store/index → baseApi.
// ---------------------------------------------------------------------------

type StateWithAuth = { auth: AuthState };

export const selectToken = (state: StateWithAuth) => state.auth.token;
export const selectUser = (state: StateWithAuth) => state.auth.user;
export const selectIsAuthenticated = (state: StateWithAuth) => state.auth.token !== null;
export const selectIsSuspended = (state: StateWithAuth) => state.auth.isSuspended;
export const selectIsAdmin = (state: StateWithAuth) => state.auth.user?.is_admin ?? false;

// A signed-in user without an org must create one before using the portal.
// Staff and superusers have no org on purpose and are exempt; they are the only
// org-less users the API reports as is_admin.
// `/me` omits the `organization` key entirely (not `null`) for a user without one,
// so check for any missing value rather than `=== null`.
export const needsOrgSetup = (user: User | null) =>
  user !== null && !user.organization && !user.is_admin;

export const selectNeedsOrgSetup = (state: StateWithAuth) => needsOrgSetup(state.auth.user);
